import type { CSSProperties } from "react";

const VARIANTS = {
  amber: "bg-data-amber text-[#3a2400]",
  /** Shipped tone — a roadmap ribbon that kept its promise (WipTag green). */
  green: "bg-data-green text-[#04250f]",
} as const;

/**
 * Classic online-shop-style diagonal corner ribbon ("Schärpe"/sash) clipped
 * across a top-right corner. Fully self-clipping (its own 110×110 box has
 * `overflow-hidden`), so it works pinned to bare content (e.g. a section's
 * top-right corner) as well as inside a card — a parent card with its own
 * rounded `overflow-hidden` additionally clips the sash's ends flush to the
 * card's rounded border, but isn't required. The nearest positioned
 * ancestor (`position: relative` or similar) determines the anchor corner
 * by default (`right: 0; top: 0`); pass `style` to override with a
 * dynamically computed anchor (e.g. a grid clearing's cell-snapped edge,
 * which needs live `top`/`right` px values, not just a static corner).
 *
 * Decorative only: `aria-hidden` plus `data-deco-avoid` so grid-mode
 * `SectionDecorations` never places a decoration underneath it.
 *
 * `amber` is the WipTag roadmap tone; `green` is its shipped counterpart
 * (used when a dated ribbon's promise lands — see McpSection).
 */
export default function CornerRibbon({
  label,
  variant = "amber",
  style,
}: {
  label: string;
  variant?: keyof typeof VARIANTS;
  /** Overrides the default `right: 0; top: 0` anchor — inline style always
   *  wins over the class, so partial overrides (e.g. just `top`) are safe. */
  style?: CSSProperties;
}) {
  return (
    <span
      aria-hidden="true"
      data-deco-avoid
      className="pointer-events-none absolute right-0 top-0 h-[110px] w-[110px] overflow-hidden"
      style={style}
    >
      <span
        className={`absolute right-[-34px] top-[26px] w-[160px] rotate-45 text-center py-1 font-jetbrains text-[10px] font-semibold uppercase tracking-[0.08em] shadow-[0_2px_6px_rgba(0,0,0,0.25)] ${VARIANTS[variant]}`}
      >
        {label}
      </span>
    </span>
  );
}
